import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useEffect, useState} from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import {ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT} from 'auth/DefaultSchemaCollaboratorsConfiguration';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {ClientSchemaField, createClientSchemaField} from 'registry/field/FieldTable';
import {isSavingFinished} from 'registry/RegistryUtil';
import CreateSchemaModal from 'schemas/CreateSchemaModal';
import {
  getInterfaceConstraintErrorsForSchema,
  InterfaceConstraintErrorDisplayDataForSchema,
} from 'schemas/schema-interface/validation/SchemaInterfaceValidationUtils';
import TagSchemaFileSettings, {useCreateSchemaParams} from 'settings/registry/TagSchemaFileSettings';
import {TagSchemaPermissionSettings} from 'settings/registry/TagSchemaPermissionSettings';
import * as TagSchemaActions from 'tag-schema/TagSchemaActions';
import {useTagSchemaSavingState} from 'tag-schema/TagSchemaClientState';
import {InlineBannerType, InlineBannerProps} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {TagSchemaModalQuery, TagSchemaModalQueryVariables} from './__generated/TagSchemaModalQuery';

type Organization = ExtractByTypename<TagSchemaModalQuery['organization'], 'OrganizationNode'>;

type Registry = TagSchemaModalQuery['registries'][number];

export interface BaseCreateTagSchemaModalProps {
  newFieldNames: Array<string>;
  onRequestClose: () => void;
  onRequestSubmit: (tagSchema: TagSchema) => void;
  ownerId: string;
}

interface QueryProps {
  organization: Organization;
  registry: Registry;
}

type CreateTagSchemaModalProps = BaseCreateTagSchemaModalProps & QueryProps;

function CreateTagSchemaModal(props: CreateTagSchemaModalProps): JSX.Element {
  const [savingState] = useTagSchemaSavingState();
  useEffect(() => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.SCHEMAS, {
      modalType: 'create_tag_schema',
    });
  }, []);
  useEffect(() => {
    if (isSavingFinished(savingState)) {
      TagSchemaActions.resetSavingState();
    }
  }, [savingState]);
  const initialFields: Array<ClientSchemaField> = props.newFieldNames.map((fieldName) =>
    createClientSchemaField(fieldName)
  );
  const [{fileSettingParams}, setCreateSchemaParams] = useCreateSchemaParams(initialFields);
  const [errorBannerProps, setErrorBannerProps] = useState(null as InlineBannerProps | null);
  const [interfaceConstraintErrors, setInterfaceConstraintErrors] = React.useState<
    Array<InterfaceConstraintErrorDisplayDataForSchema>
  >([]);
  function performValidation(): boolean {
    if (fileSettingParams.prefix.trim().length === 0) {
      setErrorBannerProps({bannerType: InlineBannerType.ERROR, text: 'Schema needs a prefix'});
      return false;
    }
    if (fileSettingParams.name.trim().length === 0) {
      setErrorBannerProps({bannerType: InlineBannerType.ERROR, text: 'Schema needs a name'});
      return false;
    }
    if (fileSettingParams.itemType == null) {
      setErrorBannerProps({bannerType: InlineBannerType.ERROR, text: 'Schema needs an entity type'});
      return false;
    }
    for (const field of fileSettingParams.fields) {
      if (_.where(fileSettingParams.fields, {name: field.name}).length > 1) {
        setErrorBannerProps({
          bannerType: InlineBannerType.ERROR,
          text: `You may not add two fields with the same name: ${field.name}`,
        });
        return false;
      }
    }
    setErrorBannerProps(null);
    return true;
  }

  async function createSchema(collaborators: Array<SchemaCollaborator>): Promise<void> {
    const createParameters = {
      ...fileSettingParams,
      itemType: fileSettingParams.itemType as TagSchemaItemType,
      registryId: props.registry.id,
      collaborators,
    };
    try {
      const schema = await TagSchemaActions.create(createParameters);
      if (!schema) {
        return;
      }
      props.onRequestSubmit(graphQLToREST('TagSchema', schema));
    } catch (e) {
      const constraintErrors = getInterfaceConstraintErrorsForSchema(e.responseJSON);
      if (constraintErrors != null && constraintErrors.length > 0) {
        setInterfaceConstraintErrors(constraintErrors);
        notifications.error(constraintErrors[0].error);
      } else {
        throw e;
      }
    }
  }

  function renderMetadataSection(): JSX.Element {
    return (
      <TagSchemaFileSettings
        ownerUrlId={props.organization.urlIdentifier}
        registryId={props.registry.id}
        tagSchemaId={null}
        createSchemaParams={{fileSettingParams}}
        setCreateSchemaParams={(params) => {
          setCreateSchemaParams(params);
          setErrorBannerProps(null);
        }}
        interfaceConstraintErrors={interfaceConstraintErrors}
      />
    );
  }

  return (
    <CreateSchemaModal
      title="Create new entity schema"
      newFieldNames={props.newFieldNames}
      onRequestClose={props.onRequestClose}
      organization={props.organization}
      createSchema={createSchema}
      isSchemaPermissionsEnabled={true}
      renderMetadataSection={renderMetadataSection}
      permissionsSettingsClass={TagSchemaPermissionSettings}
      bannerProps={errorBannerProps}
      performValidation={performValidation}
    />
  );
}

export const TAG_SCHEMA_MODAL_QUERY: DocumentNode = gql`
  query TagSchemaModalQuery($orgId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    organization(id: $orgId) {
      ... on OrganizationNode {
        id
        urlIdentifier
        ...OrganizationDefaultSchemaCollaboratorsConfiguration
      }
      ...InaccessibleInfoGraphQL
    }
    registries {
      id
      organization {
        id
      }
    }
  }
  ${ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingCreateTagSchemaModal(
  props: BaseCreateTagSchemaModalProps
): JSX.Element | null {
  const queryResult = useBenchlingQuery<TagSchemaModalQuery, TagSchemaModalQueryVariables>(
    TAG_SCHEMA_MODAL_QUERY,
    {variables: {orgId: props.ownerId}}
  );

  if (queryResult.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  }

  const organization = assertIsAccessible(queryResult.data.organization);

  const registriesForOrganization = queryResult.data.registries.filter(
    (registry) => registry.organization.id === organization.id
  );
  assert(registriesForOrganization.length === 1, 'Expected exactly one registry per organization');
  return (
    <CreateTagSchemaModal {...props} organization={organization} registry={registriesForOrganization[0]} />
  );
}
